import { Ionicons } from '@expo/vector-icons';
import React, { useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Image,
  Modal,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { TeamInvitation, TeamMember } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';

interface MembersTabProps {
  members: TeamMember[];
  pendingInvitations: TeamInvitation[];
  captainId: string;
  currentUserId: string;
  isCaptain: boolean;
  inviting: boolean;
  onInviteMember: (email: string) => void;
  onCancelInvitation: (invitationId: string) => void;
  onRemoveMember: (userId: string) => void;
  onLeaveTeam: () => void;
  onNavigateToProfile: (userId: string) => void;
  onSearchUser: (query: string) => Promise<Array<{ id: string; email: string; name?: string }>>;
  colors: ThemeColors;
}

export const MembersTab: React.FC<MembersTabProps> = ({
  members,
  pendingInvitations,
  captainId,
  currentUserId,
  isCaptain,
  inviting,
  onInviteMember,
  onCancelInvitation,
  onRemoveMember,
  onLeaveTeam,
  onNavigateToProfile,
  onSearchUser,
  colors,
}) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ id: string; email: string; name?: string }>>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ id: string; email: string; name?: string } | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = (text: string) => {
    setSearchQuery(text);
    setSelectedUser(null);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (!text.trim()) { setSearchResults([]); return; }
    debounceTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await onSearchUser(text.trim());
        setSearchResults(results.filter((r) => r.id !== currentUserId && !members.some((m) => m.userId === r.id)));
      } finally {
        setSearching(false);
      }
    }, 400);
  };

  const handleSend = () => {
    if (!selectedUser) return;
    onInviteMember(selectedUser.email);
    setModalVisible(false);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedUser(null);
  };

  return (
    <View style={{ flex: 1 }}>
      {/* Header actions */}
      <View style={{ padding: 16, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text style={{ fontSize: 14, color: colors.textSecondary }}>
          {members.length} {members.length === 1 ? t('teams.member') : t('teams.members')}
        </Text>
        {isCaptain ? (
          <TouchableOpacity
            style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: colors.primary, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8 }}
            onPress={() => setModalVisible(true)}
          >
            <Ionicons name="person-add" size={16} color={colors.primaryText} />
            <Text style={{ color: colors.primaryText, fontWeight: '600', marginLeft: 6, fontSize: 14 }}>{t('teams.addMember')}</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity
            style={{ flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: colors.error, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 8 }}
            onPress={onLeaveTeam}
          >
            <Ionicons name="exit-outline" size={16} color={colors.error} />
            <Text style={{ color: colors.error, fontWeight: '600', marginLeft: 6, fontSize: 14 }}>{t('teams.leaveTeam')}</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Pending invitations */}
      {isCaptain && pendingInvitations.length > 0 && (
        <View style={{ paddingHorizontal: 16, paddingBottom: 8 }}>
          <Text style={{ fontSize: 12, fontWeight: '700', color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            {t('teams.pendingInvitations')} ({pendingInvitations.length})
          </Text>
          {pendingInvitations.map((inv) => (
            <View
              key={inv.id}
              style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: colors.card, borderRadius: 10, borderWidth: 1, borderColor: colors.border, padding: 12, marginBottom: 8 }}
            >
              <View style={{ width: 36, height: 36, borderRadius: 18, backgroundColor: colors.primary + '20', alignItems: 'center', justifyContent: 'center', marginRight: 10 }}>
                <Ionicons name="mail-outline" size={18} color={colors.primary} />
              </View>
              <View style={{ flex: 1 }}>
                {inv.inviteeName ? (
                  <Text style={{ fontSize: 14, fontWeight: '600', color: colors.text }}>{inv.inviteeName}</Text>
                ) : null}
                <Text style={{ fontSize: 13, color: colors.textSecondary }}>
                  {inv.inviteeEmail ?? t('teams.pendingInvitation')}
                </Text>
              </View>
              <TouchableOpacity onPress={() => onCancelInvitation(inv.id)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                <Ionicons name="close-circle-outline" size={22} color={colors.error} />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      )}

      <FlatList
        data={members}
        keyExtractor={(item) => item.id}
        contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 20 }}
        ListEmptyComponent={
          <View style={{ alignItems: 'center', paddingVertical: 40 }}>
            <Ionicons name="people-outline" size={48} color={colors.textTertiary} />
            <Text style={{ color: colors.textSecondary, marginTop: 12, fontSize: 14 }}>{t('teams.noMembers')}</Text>
          </View>
        }
        renderItem={({ item }) => {
          const isThisCaptain = item.userId === captainId;
          return (
            <TouchableOpacity
              style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: colors.card, borderRadius: 10, borderWidth: 1, borderColor: colors.border, padding: 12, marginBottom: 10 }}
              onPress={() => onNavigateToProfile(item.userId)}
              activeOpacity={0.7}
            >
              <View style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border, alignItems: 'center', justifyContent: 'center', marginRight: 12 }}>
                {item.userAvatarUrl ? (
                  <Image source={{ uri: item.userAvatarUrl }} style={{ width: 44, height: 44, borderRadius: 22 }} />
                ) : (
                  <Ionicons name="person" size={22} color={colors.textTertiary} />
                )}
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 15, fontWeight: '600', color: colors.text }}>{item.userName}</Text>
                {isThisCaptain && (
                  <Text style={{ fontSize: 12, color: colors.primary, fontWeight: '600', marginTop: 2 }}>{t('teams.captain')}</Text>
                )}
              </View>
              {isCaptain && !isThisCaptain && (
                <TouchableOpacity onPress={() => onRemoveMember(item.userId)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                  <Ionicons name="remove-circle-outline" size={22} color={colors.error} />
                </TouchableOpacity>
              )}
            </TouchableOpacity>
          );
        }}
      />

      {/* Invite modal */}
      <Modal visible={modalVisible} transparent animationType="slide" onRequestClose={() => setModalVisible(false)}>
        <View style={{ flex: 1, backgroundColor: colors.overlay, justifyContent: 'flex-end' }}>
          <View style={{ backgroundColor: colors.surface, borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, maxHeight: '70%' }}>
            <Text style={{ fontSize: 18, fontWeight: '700', color: colors.text, marginBottom: 16 }}>{t('teams.addMember')}</Text>

            {/* Search input */}
            <View style={{ flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: colors.border, borderRadius: 10, paddingHorizontal: 10, backgroundColor: colors.background, marginBottom: 12 }}>
              <Ionicons name="search" size={16} color={colors.textTertiary} />
              <TextInput
                style={{ flex: 1, paddingVertical: 10, paddingHorizontal: 8, color: colors.text, fontSize: 15 }}
                placeholder={t('teams.invitePlayer')}
                placeholderTextColor={colors.textTertiary}
                value={searchQuery}
                onChangeText={handleSearchChange}
                autoCapitalize="none"
                keyboardType="email-address"
              />
              {searching && <ActivityIndicator size="small" color={colors.primary} />}
            </View>

            {/* Selected user */}
            {selectedUser && (
              <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: colors.primary + '15', borderRadius: 8, padding: 10, marginBottom: 10 }}>
                <Ionicons name="person-circle" size={24} color={colors.primary} />
                <Text style={{ flex: 1, color: colors.text, marginLeft: 8 }}>{selectedUser.name || selectedUser.email}</Text>
                <TouchableOpacity onPress={() => setSelectedUser(null)}>
                  <Ionicons name="close" size={20} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>
            )}

            {/* Results */}
            {!selectedUser && searchResults.length > 0 && (
              <FlatList
                data={searchResults}
                keyExtractor={(item) => item.id}
                style={{ maxHeight: 180, marginBottom: 12 }}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderColor: colors.divider }}
                    onPress={() => setSelectedUser(item)}
                  >
                    <Ionicons name="person-circle-outline" size={24} color={colors.textSecondary} />
                    <View style={{ marginLeft: 10 }}>
                      {item.name && <Text style={{ fontSize: 14, fontWeight: '600', color: colors.text }}>{item.name}</Text>}
                      <Text style={{ fontSize: 13, color: colors.textSecondary }}>{item.email}</Text>
                    </View>
                  </TouchableOpacity>
                )}
              />
            )}

            {/* Buttons */}
            <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
              <TouchableOpacity
                style={{ flex: 1, paddingVertical: 13, borderRadius: 10, borderWidth: 1, borderColor: colors.border, alignItems: 'center' }}
                onPress={() => { setModalVisible(false); setSearchQuery(''); setSearchResults([]); setSelectedUser(null); }}
              >
                <Text style={{ color: colors.textSecondary, fontWeight: '600' }}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={{ flex: 2, paddingVertical: 13, borderRadius: 10, alignItems: 'center', backgroundColor: selectedUser && !inviting ? colors.primary : colors.border }}
                onPress={handleSend}
                disabled={!selectedUser || inviting}
              >
                {inviting ? (
                  <ActivityIndicator color={colors.primaryText} />
                ) : (
                  <Text style={{ color: colors.primaryText, fontWeight: '700' }}>{t('teams.addMember')}</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};
